import { liteClient } from 'algoliasearch/lite';
import SearchPage from './SearchPage';
import { SearchAction } from './Models';
import { P2GButton, P2GGrid, P2GProgress, P2GText } from '@parcel2go/core-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import useExternalSearch from '~/apps/smartsend/components/smartsend/client/useExternalSearch';
import { ExternalSearchCreds } from '~/apps/smartsend/components/smartsend/client/ExternalSearchCache';
import { TranslationNamespaces } from '~/lib/constants';
import { captureException } from '~/lib/metrics';

export interface ISearchProps {
  hit?: SearchAction;
  analyticsTags?: string[];
  hideFilter?: boolean;
  hideSearch?: boolean;
}

/** How soon to try again after a background refresh fails while the current key still works. */
const REFRESH_RETRY_MS = 60 * 1000;

const Search: React.FC<ISearchProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { getExternalSearchCredentials } = useExternalSearch();
  const [creds, setCreds] = useState<ExternalSearchCreds>();
  const [client, setClient] = useState<ReturnType<typeof liteClient>>();
  const [failed, setFailed] = useState(false);

  // Read by the refresh paths, which must not act on a key the render has already replaced.
  const credsRef = useRef<ExternalSearchCreds | undefined>(undefined);
  credsRef.current = creds;

  /**
   * Fetches a key through the shared single-flight cache, which only returns a key that has not
   * expired. A failure keeps a key that still works; once there is no working key it shows the
   * retry state instead of spinning forever (B12).
   */
  const load = useCallback(async () => {
    let next: ExternalSearchCreds | undefined;
    let error: unknown;
    try {
      next = await getExternalSearchCredentials();
    } catch (e) {
      error = e;
    }

    if (next) {
      if (next.token !== credsRef.current?.token) {
        setClient(liteClient(next.application, next.token));
      }
      setCreds(next);
      setFailed(false);
      return true;
    }

    const current = credsRef.current;
    if (!current || current.validUntil <= dayjs().unix()) {
      // Reported only when it leaves the customer without search, not for each background retry.
      if (error) {
        captureException(error, 'Search token request failed', { 'search-stage': 'token' });
      }
      setCreds(undefined);
      setClient(undefined);
      setFailed(true);
    }
    return false;
  }, [getExternalSearchCredentials]);

  useEffect(() => {
    load();
  }, [load]);

  // Replace the key a margin before it expires. The page can stay open far longer than the hour a
  // key lasts; fetching once on mount left it searching with an expired key.
  useEffect(() => {
    if (!creds) {
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    const schedule = (ms: number) => {
      timer = setTimeout(refresh, Math.max(0, ms));
    };
    const refresh = async () => {
      if (await load()) {
        return;
      }
      // Still holding a working key: try again shortly, but never beyond the moment it expires.
      const untilExpiry = (credsRef.current?.validUntil ?? 0) * 1000 - Date.now();
      if (untilExpiry > 0) {
        schedule(Math.min(REFRESH_RETRY_MS, untilExpiry));
      }
    };

    schedule(creds.expires * 1000 - Date.now());

    // Timers stall while a laptop sleeps, so check again whenever the tab is shown.
    const onVisible = () => {
      if (document.visibilityState !== 'hidden' && dayjs().unix() >= creds.expires) {
        clearTimeout(timer);
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [creds, load]);

  if (failed) {
    return (
      <P2GGrid container direction="column" spacing={1}>
        <P2GGrid item>
          <P2GText colour="error">
            {t('SEARCH_KEY_UNAVAILABLE', { defaultValue: 'We could not load your orders. Please try again.' })}
          </P2GText>
        </P2GGrid>
        <P2GGrid item>
          <P2GButton color="secondary" onClick={load}>
            {t('SEARCH_KEY_RETRY', { defaultValue: 'Try again' })}
          </P2GButton>
        </P2GGrid>
      </P2GGrid>
    );
  }

  if (!creds || !client) {
    return <P2GProgress />;
  }

  return (
    <SearchPage
      hideFilter={props.hideFilter}
      hideSearch={props.hideSearch}
      client={client}
      indexName={creds.indexName}
      hit={props.hit}
      analtyicsTags={props.analyticsTags}
    />
  );
};

export default Search;
